import { useState } from 'react';
import { useCreateGameLesson } from 'hooks/useCreateGameLesson';
import { useUpdateLessonImage } from 'hooks/useUpdateLessonImage';
import { LessonForm } from "components/views/editor/models/LessonForm";
import { GameEditorData } from "components/views/editor/gameEditor/models/GameEditorData";

export const useCreateGameLessonWithVersion = () => {
    const { createGameLesson: createGameLessonWithVersionApi } = useCreateGameLesson();
    const { updateLessonImageFromFile } = useUpdateLessonImage();
    const [isCreating, setIsCreating] = useState(false);

    const createGameLessonWithVersion = async (
        lessonFormData: LessonForm,
        currentLanguage: string,
        gameEditorData: GameEditorData
    ): Promise<string | null> => {
        setIsCreating(true);

        try {
            const createResponse = await createGameLessonWithVersionApi({
                lessonName: lessonFormData.lessonName,
                tagA: lessonFormData.tagA,
                tagB: lessonFormData.tagB,
                isDraft: lessonFormData.isDraft,
                language: currentLanguage,
                name: lessonFormData.lessonVersionName,
                description: lessonFormData.description,
                maxPoints: lessonFormData.maxPoints,
                launchParams: gameEditorData.launchParams?.trim() || null,
                fileId: gameEditorData.fileId!,
                events: gameEditorData.events
                    .filter((e) => e.event.trim() !== "")
                    .map((e) => ({
                        eventKey: e.event,
                        points: Math.max(0, Math.min(lessonFormData.maxPoints, e.points))
                    }))
            });

            const newLessonId = createResponse?.success && createResponse.data
                ? createResponse.data
                : null;

            if (!newLessonId) return null;

            if (lessonFormData.image) {
                const imageUpdateSuccess = await updateLessonImageFromFile(
                    newLessonId,
                    lessonFormData.image.file
                );
                if (!imageUpdateSuccess) return null;
            }

            return newLessonId;
        } catch {
            return null;
        } finally {
            setIsCreating(false);
        }
    };

    return { createGameLessonWithVersion, isCreating };
};
